import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { withoutCellPrefix } from '@/lib/global/market-routing';

const STORAGE_KEY = 'ai-chat-open';

export type AIChatOpenSource = 'default' | 'support' | 'finance' | 'settings';

/** When `openSource` is null, route picks the agent (matches floating trigger behaviour). */
export function resolveAIChatAgentSource(openSource: AIChatOpenSource | null, pathname: string): AIChatOpenSource {
  // The settings preview is only opened explicitly (eye button) and only makes sense on settings pages.
  if (openSource === 'settings' && !pathname.startsWith('/settings')) return 'default';
  if (openSource !== null) return openSource;
  if (withoutCellPrefix(pathname).startsWith('/app/finances')) return 'finance';
  if (pathname.startsWith('/app/support')) return 'support';
  return 'default';
}

interface AIChatContextValue {
  isOpen: boolean;
  openSource: AIChatOpenSource | null;
  /** Effective agent for UI and finance hook sync when `openSource` is null (route-based). */
  resolvedOpenSource: AIChatOpenSource;
  openMetadata: Record<string, unknown> | null;
  showTrigger: boolean;
  setShowTrigger: (show: boolean) => void;
  open: (options?: { source?: AIChatOpenSource; metadata?: Record<string, unknown> }) => void;
  close: () => void;
  toggle: () => void;
}

const AIChatContext = createContext<AIChatContextValue | undefined>(undefined);

export function AIChatProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [openSource, setOpenSource] = useState<AIChatOpenSource | null>(null);
  const [openMetadata, setOpenMetadata] = useState<Record<string, unknown> | null>(null);
  const [showTrigger, setShowTrigger] = useState(true);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(isOpen));
    } catch {
      // ignore
    }
  }, [isOpen]);

  const resolvedOpenSource = useMemo(() => resolveAIChatAgentSource(openSource, location.pathname), [openSource, location.pathname]);

  const open = useCallback((options?: { source?: AIChatOpenSource; metadata?: Record<string, unknown> }) => {
    setIsOpen(true);
    setOpenSource(options?.source ?? null);
    setOpenMetadata(options?.metadata ?? null);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
    setOpenSource(null);
    setOpenMetadata(null);
  }, []);
  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) {
        setOpenSource(null);
        setOpenMetadata(null);
      }
      return !prev;
    });
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      openSource,
      resolvedOpenSource,
      openMetadata,
      showTrigger,
      setShowTrigger,
      open,
      close,
      toggle,
    }),
    [isOpen, openSource, resolvedOpenSource, openMetadata, showTrigger, open, close, toggle]
  );

  return <AIChatContext.Provider value={value}>{children}</AIChatContext.Provider>;
}

export function useAIChat() {
  const context = useContext(AIChatContext);
  if (!context) {
    throw new Error('useAIChat must be used within an AIChatProvider');
  }
  return context;
}

/** Like useAIChat, but null outside an AIChatProvider so optional entry points can render nothing. */
export function useAIChatSafe() {
  return useContext(AIChatContext);
}

/**
 * Hook to hide the floating chat trigger while the calling component is mounted.
 * The trigger is automatically restored when the component unmounts.
 */
export function useHideChatTrigger() {
  const { setShowTrigger } = useAIChat();

  useEffect(() => {
    setShowTrigger(false);
    return () => setShowTrigger(true);
  }, [setShowTrigger]);
}
